import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CONFIG } from '../config';
import { FiShield, FiZap, FiArrowRight, FiLock, FiUnlock } from 'react-icons/fi';

function Pools() {
  const navigate = useNavigate();
  const [poolData, setPoolData] = useState({
    regular: { apy: 0 },
    protected: { apy: 0 }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPoolData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch('https://api.lulo.fi/v1/pool.getPools', {
          method: 'GET',
          headers: {
            'x-api-key': CONFIG.LULO_API_KEY,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) throw new Error('Failed to fetch pool data');
        const data = await response.json();
        
        // Format APYs as percentages with 2 decimal places
        const formattedData = {
          regular: {
            apy: data.regular?.apy ? (data.regular.apy * 100).toFixed(2) : '0.00'
          },
          protected: {
            apy: data.protected?.apy ? (data.protected.apy * 100).toFixed(2) : '0.00'
          }
        };
        
        setPoolData(formattedData);
      } catch (err) {
        console.error('Error fetching pool data:', err);
        setError('Failed to load pool data. Using default values.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPoolData();
  }, []);

  return (
    <div className="pools-page-container">
      <div className="pools-content-wrapper">
        <h1>DeFi Yield Pools</h1>
        <div className="pools-description">
          Explore curated DeFi pools to earn yield on your assets. Compare APYs, asset types, and risks to find the best fit for your investment goals.
        </div>
        <div className="available-pools-title">Available Pools</div>
        <div className="pools-table-wrapper">
          <div className="pools-grid">
            {/* Regular Pool Card */}
            <div 
              className="pool-card regular-pool"
              onClick={() => navigate('/regular')}
            >
              <div className="pool-header">
                <div className="pool-icon">
                  <FiZap size={24} />
                </div>
                <h3>Regular Pool</h3>
                <div className="pool-badge">Standard</div>
              </div>
              
              <div className="pool-details">
                <div className="pool-apy">
                  <span className="label">APY</span>
                  <span className="value highlight">
                    {isLoading ? '...' : `${poolData.regular.apy}%`}
                  </span>
                </div>
                
                <div className="pool-features">
                  <div className="feature">
                    <FiUnlock className="feature-icon" />
                    <span>No Lockup</span>
                  </div>
                  <div className="feature">
                    <div className="risk-dot low" />
                    <span>Low Risk</span>
                  </div>
                </div>
                
                <div className="pool-asset">
                  <span>USDC</span>
                </div>
              </div>
              
              <div className="pool-footer">
                <span>Start Earning</span>
                <FiArrowRight className="arrow-icon" />
              </div>
            </div>
            
            {/* Protected Pool Card */}
            <div 
              className="pool-card protected-pool"
              onClick={() => navigate('/lulo')}
            >
              <div className="pool-header">
                <div className="pool-icon">
                  <FiShield size={24} />
                </div>
                <h3>Protected Pool</h3>
                <div className="pool-badge featured">Featured</div>
              </div>
              
              <div className="pool-details">
                <div className="pool-apy">
                  <span className="label">APY</span>
                  <span className="value highlight">
                    {isLoading ? '...' : `${poolData.protected.apy}%`}
                  </span>
                </div>
                
                <div className="pool-features">
                  <div className="feature">
                    <FiLock className="feature-icon" />
                    <span>Principal Protection</span>
                  </div>
                  <div className="feature">
                    <div className="risk-dot low" />
                    <span>Low Risk</span>
                  </div>
                </div>
                
                <div className="pool-asset">
                  <span>USDC</span>
                </div>
              </div>
              
              <div className="pool-footer">
                <span>Start Earning</span>
                <FiArrowRight className="arrow-icon" />
              </div>
            </div>
          </div>
        </div>
        
        <style jsx>{`
          .pools-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
            gap: 1.5rem;
            margin-top: 1.5rem;
          }
          
          .pool-card {
            background: white;
            border-radius: 12px;
            padding: 1.5rem;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
            transition: all 0.2s ease;
            cursor: pointer;
            border: 1px solid #e5e7eb;
            display: flex;
            flex-direction: column;
            height: 100%;
          }
          
          .pool-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
            border-color: #3b82f6;
          }
          
          .pool-header {
            display: flex;
            align-items: center;
            margin-bottom: 1.25rem;
            position: relative;
          }
          
          .pool-icon {
            width: 40px;
            height: 40px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 12px;
          }
          
          .regular-pool .pool-icon {
            background-color: #e0f2fe;
            color: #0ea5e9;
          }
          
          .protected-pool .pool-icon {
            background-color: #dbeafe;
            color: #2563eb;
          }
          
          .pool-header h3 {
            margin: 0;
            font-size: 1.25rem;
            font-weight: 600;
            color: #111827;
          }
          
          .pool-badge {
            position: absolute;
            top: -8px;
            right: -8px;
            background: #f3f4f6;
            color: #4b5563;
            font-size: 0.75rem;
            font-weight: 500;
            padding: 2px 8px;
            border-radius: 12px;
          }
          
          .pool-badge.featured {
            background: #dbeafe;
            color: #2563eb;
          }
          
          .pool-apy {
            margin-bottom: 1.25rem;
          }
          
          .pool-apy .label {
            display: block;
            font-size: 0.875rem;
            color: #6b7280;
            margin-bottom: 4px;
          }
          
          .pool-apy .value {
            font-size: 1.75rem;
            font-weight: 700;
            color: #111827;
          }
          
          .highlight {
            color: #2563eb;
          }
          
          .pool-features {
            margin: 1.25rem 0;
            display: flex;
            flex-direction: column;
            gap: 10px;
          }
          
          .feature {
            display: flex;
            align-items: center;
            font-size: 0.875rem;
            color: #4b5563;
          }
          
          .feature-icon {
            margin-right: 8px;
            color: #6b7280;
          }
          
          .risk-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            margin-right: 8px;
          }
          
          .risk-dot.low {
            background-color: #10b981;
          }
          
          .pool-asset {
            margin-top: auto;
            padding-top: 1rem;
            border-top: 1px solid #e5e7eb;
            color: #6b7280;
            font-size: 0.875rem;
          }
          
          .pool-footer {
            margin-top: 1.25rem;
            padding-top: 1rem;
            border-top: 1px solid #e5e7eb;
            display: flex;
            justify-content: space-between;
            align-items: center;
            color: #3b82f6;
            font-weight: 500;
          }
          
          .arrow-icon {
            transition: transform 0.2s ease;
          }
          
          .pool-card:hover .arrow-icon {
            transform: translateX(4px);
          }
          
          @media (max-width: 640px) {
            .pools-grid {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
        
      </div>
      {error && (
        <div className="error-message" style={{ color: 'red', marginTop: '1rem' }}>
          {error}
        </div>
      )}
    </div>
  );
}

export default Pools;